/**
 * Mobile-Responsive IdleWarningModal Component
 * Features:
 * - Bottom sheet on mobile (< 640px), centered dialog on desktop
 * - Safe-area support for notches and home indicators
 * - Focus trap and scroll lock
 * - Dark mode and reduced motion support
 * - Full accessibility compliance
 */

import React, { useEffect, useRef, useCallback } from 'react';
import useRealTimeCountdown from '../hooks/useRealTimeCountdown';

export default function IdleWarningModalResponsive({ open, logoutAtMs, onStay, onLogout }) {
  const seconds = useRealTimeCountdown({ 
    endAtMs: logoutAtMs, 
    onEnd: onLogout, 
    visibleUpdateMs: 250, 
    hiddenUpdateMs: 1000 
  });

  const dialogRef = useRef(null);
  const firstBtnRef = useRef(null);
  const lastBtnRef = useRef(null);

  // Scroll lock when open (prevents background scroll on mobile)
  useEffect(() => {
    if (!open) return;
    
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevTouch = body.style.touchAction;
    const prevScroll = body.style.overscrollBehavior;
    
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.overscrollBehavior = 'contain';
    
    return () => { 
      body.style.overflow = prevOverflow; 
      body.style.touchAction = prevTouch; 
      body.style.overscrollBehavior = prevScroll; 
    };
  }, [open]);

  // Focus trap + initial focus
  useEffect(() => {
    if (open) {
      const to = setTimeout(() => {
        firstBtnRef.current?.focus();
      }, 0);
      return () => clearTimeout(to);
    }
  }, [open]);

  // Focus trap keyboard handler
  const onKeyDown = useCallback((e) => {
    if (e.key !== 'Tab') return;
    
    const first = firstBtnRef.current;
    const last = lastBtnRef.current;
    if (!first || !last) return;
    
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  // Handle escape key (intentionally disabled for security)
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && open) {
        // Intentionally not calling onLogout for security
        // User must make an explicit choice
        event.preventDefault();
      }
    };

    if (open) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [open]);

  if (!open) return null;

  return (
    <>
      {/* Modal Container */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="idle-title"
        aria-describedby="idle-desc"
        className="idle-modal-container"
        onKeyDown={onKeyDown}
      >
        {/* Backdrop */}
        <div className="idle-modal-backdrop" />

        {/* Container: centered on all screen sizes */}
        <div className="idle-modal-positioner">
          <div
            ref={dialogRef}
            className="idle-modal-content"
            style={{ maxWidth: 'min(92%, 28rem)' }}
          >
            {/* Header */}
            <div className="idle-modal-header">
              <div className="idle-modal-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
              <div className="idle-modal-title-section">
                <h2 id="idle-title" className="idle-modal-title">
                  Are you still there?
                </h2>
                <p className="idle-modal-subtitle">
                  We noticed you haven't been active for a while
                </p>
              </div>
            </div>

            {/* Body */}
            <div className="idle-modal-body">
              <p id="idle-desc" className="idle-modal-description">
                You will be automatically signed out in{' '}
                <strong className="idle-countdown-text">{seconds}</strong> seconds due to inactivity.
                Click "Stay logged in" to continue your session.
              </p>

              {/* Countdown Display */}
              <div className="idle-countdown-container">
                <div className="idle-countdown-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <polyline points="12,6 12,12 16,14"/>
                  </svg>
                </div>
                <div className="idle-countdown-text-section">
                  <div className="idle-countdown-label">Time remaining</div>
                  <div className="idle-countdown-value">{seconds} seconds</div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="idle-modal-actions">
              <button
                ref={firstBtnRef}
                onClick={onLogout}
                className="idle-btn idle-btn-secondary"
                data-idle-modal="logout-button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16,17 21,12 16,7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Log out now
              </button>
              <button
                ref={lastBtnRef}
                onClick={onStay}
                className="idle-btn idle-btn-primary"
                data-idle-modal="stay-button"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Stay logged in
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Styles */}
      <style>{`
        .idle-modal-container {
          position: fixed;
          inset: 0;
          z-index: 9999;
        }

        .idle-modal-backdrop {
          position: absolute;
          inset: 0;
          background: var(--background-overlay);
          backdrop-filter: blur(1px);
          transition: opacity 200ms ease-out;
        }

        @media (prefers-reduced-motion: reduce) {
          .idle-modal-backdrop {
            transition: none;
          }
        }

        /* Centered positioning on all screen sizes */
        .idle-modal-positioner {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
        }

        .idle-modal-content {
          background: var(--background-primary);
          color: var(--contrast-primary);
          width: 100%;
          border-radius: 0;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
          padding: 1rem;
          padding-bottom: calc(1rem + env(safe-area-inset-bottom));
          padding-top: calc(1rem + env(safe-area-inset-top));
          transform: translateY(0);
          animation: slideUp 200ms ease-out;
          position: relative;
        }
        @media (min-width: 640px) {
          .idle-modal-content {
            padding: 1.5rem;
            padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
            padding-top: calc(1.5rem + env(safe-area-inset-top));
            animation: fadeInScale 180ms ease-out;
            max-height: 90vh;
            overflow-y: auto;
          }
        }

        /* Dark mode support */
        @media (prefers-color-scheme: dark) {
          .idle-modal-content {
            background: var(--background-primary);
            color: var(--contrast-primary);
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .idle-modal-content {
            animation: none;
          }
        }

        /* Animations */
        @keyframes slideUp {
          from {
            transform: translateY(12%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes fadeInScale {
          from {
            transform: scale(0.98);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        /* Header */
        .idle-modal-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .idle-modal-icon {
          width: 48px;
          height: 48px;
          border-radius: 0;
          background: var(--background-tertiary);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--contrast-secondary);
          flex-shrink: 0;
        }

        .idle-modal-title-section {
          flex: 1;
        }

        .idle-modal-title {
          font-size: 1.125rem;
          font-weight: 600;
          line-height: 1.4;
          margin: 0 0 0.25rem 0;
          color: inherit;
        }

        @media (min-width: 640px) {
          .idle-modal-title {
            font-size: 1.25rem;
          }
        }

        .idle-modal-subtitle {
          font-size: 0.875rem;
          color: var(--contrast-secondary);
          margin: 0;
          line-height: 1.4;
        }

        /* Body */
        .idle-modal-body {
          margin-bottom: 1.5rem;
        }

        .idle-modal-description {
          font-size: 0.875rem;
          line-height: 1.5;
          margin: 0 0 1rem 0;
          color: inherit;
        }

        @media (min-width: 640px) {
          .idle-modal-description {
            font-size: 1rem;
          }
        }

        .idle-countdown-container {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 1rem;
          background: var(--background-tertiary);
          border-radius: 0;
          border: 1px solid var(--border-color);
        }

        .idle-countdown-icon {
          color: var(--contrast-secondary);
          flex-shrink: 0;
        }

        .idle-countdown-text-section {
          flex: 1;
        }

        .idle-countdown-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--contrast-secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.25rem;
        }

        .idle-countdown-value {
          font-size: 1.125rem;
          font-weight: 600;
          color: var(--contrast-primary);
        }

        .idle-countdown-text {
          color: var(--warning-color);
          font-weight: 700;
        }

        /* Actions */
        .idle-modal-actions {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        @media (min-width: 640px) {
          .idle-modal-actions {
            flex-direction: row;
            justify-content: flex-end;
            gap: 0.75rem;
          }
        }

        .idle-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          border-radius: 0;
          font-size: 0.875rem;
          font-weight: 500;
          line-height: 1.25;
          border: 1px solid transparent;
          cursor: pointer;
          transition: all 150ms ease-out;
          min-height: 44px; /* Minimum touch target size */
          text-decoration: none;
        }

        @media (prefers-reduced-motion: reduce) {
          .idle-btn {
            transition: none;
          }
        }

        .idle-btn:focus {
          outline: 2px solid var(--info-color);
          outline-offset: 2px;
        }

        .idle-btn-secondary {
          background: var(--background-secondary);
          color: var(--contrast-secondary);
          border-color: var(--border-color);
        }

        .idle-btn-secondary:hover {
          background: var(--background-tertiary);
          border-color: var(--border-dark);
        }

        .idle-btn-primary {
          background: var(--primary-color);
          color: var(--text-inverse);
          border-color: var(--primary-color);
        }

        .idle-btn-primary:hover {
          background: var(--primary-dark);
          border-color: var(--primary-dark);
        }

        /* Ensure minimum height for landscape mobile */
        @media (max-height: 500px) and (orientation: landscape) {
          .idle-modal-content {
            min-height: min(40vh, 280px);
          }
        }
      `}</style>
    </>
  );
}
